import { Transform, TransformGroup } from '4d-mapper';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';
import { BaseComponent } from '@src/app/commons/BaseComponent';
import { MapService } from '@src/app/commons/map.service';
import { PluginManagerService } from '@src/app/service/plugin-manager.service';
import { XsAwait } from '@src/app/xs/controllers/await.class';
import { Cartesian3, Cesium3DTileset, ClippingPlane, ClippingPlaneCollection, Entity, EntityCollection, PlaneGraphics } from 'cesium';
import ScreenSpaceEventHandler from 'cesium/Source/Core/ScreenSpaceEventHandler';
import { map } from 'rxjs/operators';
import { XsApplicationEventsService } from '../../events/application-events.service';
import { UiManagerService } from '../ui-manager.service';

const Cesium = (<any>window).Cesium;

type PlaneAxis = 'x' | 'y';
interface MovementHandler {
  up?: ScreenSpaceEventHandler;
  down?: ScreenSpaceEventHandler;
  left?: ScreenSpaceEventHandler;
  right?: ScreenSpaceEventHandler;
  mouse?: ScreenSpaceEventHandler;
}

interface PlanePosition {
  x: number;
  y: number;
}

interface CartesianAxis {
  x: Cartesian3;
  y: Cartesian3;
}

@Component({
  selector: 'fdm-tile-profiler',
  templateUrl: './tile-profiler.component.html',
  styleUrls: ['./tile-profiler.component.scss'],
})
export class TileProfilerComponent extends BaseComponent implements OnInit, OnDestroy {
  public profilerActive: boolean = false;

  private movementHandler: MovementHandler = {};
  private planePos: PlanePosition = { x: 0.0, y: 0.0 };
  private cartesians: CartesianAxis = { x: new Cesium.Cartesian3(1.0, 0.0, 0.0), y: new Cesium.Cartesian3(0.0, 0.0, -1.0) };
  private planeEntities: Entity[] = [];
  private clippingPlanes?: ClippingPlaneCollection;
  private selectedAxis: PlaneAxis = 'x';
  private selectedPlane?: PlaneGraphics;
  private primitives?: Entity[];
  private transforms?: Transform[];
  private layerOptions?: { label: string; layer: Cesium3DTileset }[];
  private selectedLayer?: Cesium3DTileset;

  constructor(
    public router: Router,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    public uiManager: UiManagerService,
    public applicationEvents: XsApplicationEventsService,
    public pluginManager: PluginManagerService,
    public mapService: MapService
  ) {
    super(router, snackBar, dialog);
  }

  start(transforms: TransformGroup) {
    console.log('starting profiler', transforms);
    if (transforms) {
      this.queryTransforms(transforms).subscribe((transforms) => {
        this.primitives = this.mapService.viewer.scene.primitives._primitives;
        this.transforms = transforms;

        this.setupLayerSelector();
        this.setupMovementHandlers();
        this.setupClippingPlanes();

        this.profilerActive = true;
      });
    } else {
      console.warn('Unable to start Tile Profiler');
    }
  }

  stop() {
    // TODO: Clean everything up.
    if (this.profilerActive) {
      console.log('stopping profiler');
      this.destroyAllPlanes();
      this.destroyMovementHandlers();
      this.profilerActive = false;
    }
  }

  waitForReady() {
    return new Promise(() => {
      XsAwait.until(['mapService.viewer.scene', 'mapService.scene', 'mapService.currentProject'], this).then(() => {
        return setTimeout(() => {
          // console.log('map service current project and scene ready!');
        }, 1);
      });
    });
  }

  updateTransforms(transforms: Transform[]) {
    this.waitForReady().then(() => {
      // console.log('updating transforms', transforms);
    });
  }

  updateSelectedLayer(layer: Transform) {
    // console.log('updating selected layer', layer['transform_id']);
  }

  updatePlane(plane: any, axis: PlaneAxis = this.selectedAxis) {
    if (plane.distance !== this.planePos[axis]) {
      plane.distance = this.planePos[axis];
    }
    return plane;
  }

  queryTransforms(transforms: TransformGroup) {
    return transforms.children.queryObservable().pipe(
      map((results) => {
        const transforms = results.items;
        const canProfile = (t: Transform) => t['fdm_features'].canHeightAdjust;
        return transforms.filter((t) => canProfile(t));
      })
    );
  }

  setupLayerSelector() {
    this.layerOptions = this.primitives
      .filter((p: any) => p.transform_id)
      .map((p: any) => {
        const correspondingTransform = this.transforms.find((t) => t.id === p.transform_id);
        return { label: correspondingTransform.name, layer: p as Cesium3DTileset };
      });

    /* Uncomment the below condition to set a layer on init. */
    // if (this.layerOptions.length) {
    //   this.selectedLayer = this.layerOptions[0].layer;
    // }
  }

  setupMovementHandlers() {
    console.log('setting up movement handlers');

    // Grabbing the plane when left mouse down.
    this.movementHandler.down = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
    this.movementHandler.down.setInputAction((movement: ScreenSpaceEventHandler.PositionedEvent) => {
      const selectedObject = this.mapService.viewer.scene.pick(movement.position);
      if (Cesium.defined(selectedObject) && Cesium.defined(selectedObject.id) && Cesium.defined(selectedObject.id.plane)) {
        this.selectedPlane = this.planeEntities.find((pe) => pe.id === selectedObject.id._id).plane;
        this.selectedPlane.material = Cesium.Color.YELLOW.withAlpha(0.05);
        this.selectedPlane.outlineColor = Cesium.Color.YELLOW;
        this.mapService.viewer.scene.screenSpaceCameraController.enableInputs = false;
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    // Releasing the plane when left mouse up.
    this.movementHandler.up = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
    this.movementHandler.up.setInputAction(() => {
      if (Cesium.defined(this.selectedPlane)) {
        this.selectedPlane.material = Cesium.Color.WHITE.withAlpha(0.1);
        this.selectedPlane.outlineColor = Cesium.Color.WHITE;
        this.selectedPlane = undefined;
      }
      this.mapService.viewer.scene.screenSpaceCameraController.enableInputs = true;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    this.updateMouseMovementAxis();
  }

  updateMouseMovementAxis() {
    this.movementHandler.mouse = new Cesium.ScreenSpaceEventHandler(this.mapService.viewer.scene.canvas);
    this.movementHandler.mouse.setInputAction((movement: ScreenSpaceEventHandler.MotionEvent) => {
      if (Cesium.defined(this.selectedPlane)) {
        const delta = movement.startPosition[this.selectedAxis] - movement.endPosition[this.selectedAxis];
        this.planePos[this.selectedAxis] += delta;
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }

  destroyMovementHandlers() {
    return Object.values(this.movementHandler)
      .filter((v) => !!v)
      .map((handler: ScreenSpaceEventHandler) => {
        handler.destroy();
        return handler;
      })
      .every((handler: ScreenSpaceEventHandler) => handler.isDestroyed());
  }

  setupClippingPlanes() {
    console.log('Setting up clipping planes');

    if (this.selectedLayer) {
      this.updateClippingPlanes();
    }
  }

  updateClippingPlanes() {
    if (this.selectedLayer) {
      this.resetPlanePosition();
      this.removeAllClippingPlanes();
      this.removeAllPlaneEntities();
    }

    this.clippingPlanes = new Cesium.ClippingPlaneCollection({
      planes: [new Cesium.ClippingPlane(this.cartesians[this.selectedAxis], 0.0)],
      edgeWidth: 1.0,
    });

    // console.log('clipping planes before assignment', this.selectedLayer.clippingPlanes);
    this.selectedLayer.clippingPlanes = this.clippingPlanes;
    // console.log('clipping planes after assignment', this.selectedLayer.clippingPlanes);

    const boundingSphere = this.selectedLayer.boundingSphere;
    const radius = boundingSphere.radius;

    // console.log('bounding sphere', boundingSphere);
    // console.log((this.clippingPlanes as any)._planes);

    const planesArray = (this.clippingPlanes as any)._planes;
    this.planeEntities = planesArray.map((plane: ClippingPlane) => {
      return this.mapService.viewer.entities.add({
        position: boundingSphere.center,
        plane: {
          dimensions: new Cesium.Cartesian2(radius * 1.25, radius * 1.25),
          material: Cesium.Color.WHITE.withAlpha(0.1),
          plane: new Cesium.CallbackProperty(() => this.updatePlane(plane), false),
          outline: true,
          outlineColor: Cesium.Color.WHITE,
        },
      });
    });

    this.flyToSelectedLayer();
  }

  flyToSelectedLayer() {
    const selectedOption = this.layerOptions.find((l) => l.layer === this.selectedLayer);
    const correspondingTransform = this.transforms.find((t) => t.name === selectedOption.label);
    if (selectedOption && correspondingTransform) {
      this.mapService.helper.sendCameraTo(correspondingTransform);
    }
  }

  updateSelectedAxis() {
    this.updateMouseMovementAxis();
    this.updateClippingPlanes();
  }

  resetPlanePosition() {
    this.planePos = { x: 0.0, y: 0.0 };
  }

  removeAllClippingPlanes() {
    if (this.selectedLayer && this.selectedLayer.clippingPlanes) {
      this.selectedLayer.clippingPlanes.removeAll();
    }
    this.layerOptions
      .map((lo) => lo.layer)
      .forEach((p: Cesium3DTileset) => {
        if (p.clippingPlanes) {
          p.clippingPlanes.removeAll();
        }
      });
  }

  removeAllPlaneEntities() {
    // Remove all of the planes drawn to the canvas.
    this.planeEntities.forEach((e: Entity) => {
      const viewerEntities = this.mapService.viewer.entities as EntityCollection;
      if (viewerEntities && viewerEntities.contains(e)) {
        this.mapService.viewer.entities.remove(e);
      }
    });
  }

  destroyAllPlanes() {
    this.removeAllClippingPlanes();
    this.removeAllPlaneEntities();
    this.resetPlanePosition();
    this.selectedAxis = 'x';
    this.clippingPlanes = new Cesium.ClippingPlaneCollection({ planes: [], enabled: false });
    this.selectedPlane = undefined;
    this.selectedLayer = undefined;
  }
}
